import { Dispatch, SetStateAction, useCallback } from 'react';
import { FieldValues, UseFormGetValues } from 'react-hook-form';
import { differenceInDays } from 'date-fns';

import { GroupSelectOptionsInterface } from '@components/common';

import { getTimeAggregationType } from '@utils/kpi-utils/common';
import {
	endOfDayInUTC,
	prepareDateForRequest,
	startOfDayInUTC,
} from '@utils/kpi-utils/date';
import { getDateSelectionParameters } from '@components/Kpi/Parameters/utils';

import { IEditPresetDTO, IPresetDTO } from '@interfaces/kpi/dto/kpiDTO';
import { IUpdatePresetDTO } from '@interfaces/kpi/dto/kpiPresetsDTO';
import { IKPISite, IPreset } from '@interfaces/kpi/kpiCommon';
import { IGroupSelectItem } from '@components/common/GroupSelect/GroupSelect.types';
import { TSelectOptionType } from '@components/common/Select/Select.types';

import {
	ETechnologiesValues,
	TTechnologiesValues,
	TVendorsValues,
} from '@enums/common';

interface UseSaveLogicProps {
	getValues: UseFormGetValues<FieldValues>;
	vendor: { name?: TVendorsValues; value: TVendorsValues };
	presetValue?: TSelectOptionType & IPreset;
	selectedPresetValue?: TSelectOptionType<string>;
	formEditablePreset: IPresetDTO;
	openPresetForm: (presetDate: IPresetDTO) => void;
	updatePreset: (values: IUpdatePresetDTO) => void;
	closePresetForm: () => void;
	setStateSelectedPreset: Dispatch<SetStateAction<IPresetDTO | undefined>>;
}

export const useSaveLogic = ({
	getValues,
	vendor,
	presetValue,
	selectedPresetValue,
	formEditablePreset,
	openPresetForm,
	updatePreset,
	closePresetForm,
	setStateSelectedPreset,
}: UseSaveLogicProps) => {
	const getSites = (options: Array<GroupSelectOptionsInterface>, technology: TTechnologiesValues): IKPISite[] => {
		return options.reduce<Array<IKPISite>>((acc, option) => {
			const processItems = (items: Array<IGroupSelectItem>, parentSiteId: string): IKPISite => {
				const collectedCellIds: string[] = [];

				const collectCellIds = (items: Array<IGroupSelectItem>, siteId: string) => {
					items.forEach(item => {
						if (item.id) {
							collectedCellIds.push(item.id);
						}
						if (item.items) {
							collectCellIds(item.items, siteId);
						}
					});
				};

				collectCellIds(items, parentSiteId);

				return {
					cellIds: collectedCellIds,
					siteId: parentSiteId,
					technology,
				};
			};

			if (option.items.some(item => item.category && item.items)) {
				option.items.forEach(subCategory => {
					if (subCategory.category) {
						acc.push(processItems(subCategory.items || [], subCategory.category.name));
					}
				});
			} else {
				const cellIds = option.items.map(item => item.id || '');
				acc.push({
					cellIds,
					siteId: option.category.name,
					technology,
				});
			}

			return acc;
		}, []);
	};

	const prepareDataForSubmit = useCallback((values: FieldValues) => {
		const diffInDays = differenceInDays(values.to, values.from);

		const timeAggregationType = getTimeAggregationType(diffInDays);

		const sites: IKPISite[] = [];

		if (values.sitesT_4G_FDD.length) {
			const sites4G_FDD = getSites(values.sitesT_4G_FDD, ETechnologiesValues.T_4G_FDD);
			sites4G_FDD.forEach(site => sites.push(site));
		}

		if (values.sitesT_4G_TDD.length) {
			const sites4G_TDD = getSites(values.sitesT_4G_TDD, ETechnologiesValues.T_4G_TDD);
			sites4G_TDD.forEach(site => sites.push(site));
		}

		if (values.sitesT_5G.length) {
			const sites5G = getSites(values.sitesT_5G, ETechnologiesValues.T_5G);
			sites5G.forEach(site => sites.push(site));
		}

		return {
			timeAggregationType,
			sites,
		};
	}, []);

	const handleCreatePreset = useCallback(() => {
		const values = getValues();

		const { sites } = prepareDataForSubmit(values);

		const kpiIds = [...values.treeKpiIds, ...values.treeCountersIds];
		const dateFromUTC = prepareDateForRequest(values.from, startOfDayInUTC);
		const dateToUTC = prepareDateForRequest(values.to, endOfDayInUTC);

		const dateSelectionParameters = getDateSelectionParameters({
			isOranVendor: true,
			values,
			dateFromUTC,
			dateToUTC,
		});

		const presetData: IPresetDTO = {
			vendor: vendor.value,
			aggregationType: values.aggregationType,
			kpiIds: kpiIds,
			sites: sites.length ? sites : null,
			granularity: values.granularity?.value,
			isAutomaticGranularity: values.isAutomaticGranularity,
			isShared: values.isShared,
			...dateSelectionParameters,
		};

		openPresetForm(presetData);
	}, [
		getValues,
		openPresetForm,
		prepareDataForSubmit,
		vendor?.value,
	]);

	const handleUpdatePreset = useCallback(() => {
		if (!presetValue?.id || typeof presetValue.id !== 'string' || !presetValue?.name || !formEditablePreset) {
			return;
		}

		const values = getValues();

		const { sites } = prepareDataForSubmit(values);

		const kpiIds = [...values.treeKpiIds, ...values.treeCountersIds];
		const dateFromUTC = prepareDateForRequest(values.from, startOfDayInUTC);
		const dateToUTC = prepareDateForRequest(values.to, endOfDayInUTC);

		const dateSelectionParameters = getDateSelectionParameters({
			isOranVendor: true,
			values,
			dateFromUTC,
			dateToUTC,
		});

		const presetData: IEditPresetDTO = {
			id: presetValue.id,
			name: presetValue.name,
			vendor: vendor.value,
			aggregationType: values.aggregationType,
			kpiIds: kpiIds,
			sites: sites.length ? sites : null,
			granularity: values.granularity?.value,
			isAutomaticGranularity: values.isAutomaticGranularity,
			isShared: values.isShared,
			...dateSelectionParameters,
		};

		const updateSelectedPresetOnDashboard = selectedPresetValue?.id === presetValue.id
			? setStateSelectedPreset
			: undefined;

		updatePreset({
			data: presetData,
			onSuccess: (data) => {
				if (updateSelectedPresetOnDashboard) {
					updateSelectedPresetOnDashboard(data);
				}

				closePresetForm();
			},
		});
	}, [
		selectedPresetValue?.id,
		setStateSelectedPreset,
		closePresetForm,
		getValues,
		prepareDataForSubmit,
		presetValue?.id,
		presetValue?.name,
		formEditablePreset,
		updatePreset,
		vendor?.value,
	]);

	return {
		handleCreatePreset,
		handleUpdatePreset,
	};
};
